import React, { useState, useEffect } from 'react';
import { PayoffCalculator } from './components/PayoffCalculator';
import { BuyNewHomeCalculator } from './components/BuyNewHomeCalculator';
import { MortgageCostEstimator } from './components/MortgageCostEstimator';
import { PMIRemovalCalculator } from './components/PMIRemovalCalculator';
import { CalculatorMode } from './types';

const App: React.FC = () => {
    // Initialize state function to check URL params before first render
    const [mode, setMode] = useState<CalculatorMode>(() => {
        const params = new URLSearchParams(window.location.search);
        const tabParam = params.get('tab');
        
        if (tabParam === 'payoff') return CalculatorMode.PAYOFF;
        if (tabParam === 'cost_estimator') return CalculatorMode.COST_ESTIMATOR;
        if (tabParam === 'pmi_removal') return CalculatorMode.PMI_REMOVAL;
        
        // DEFAULT is now BUY_NEW
        return CalculatorMode.BUY_NEW;
    });

    const [darkMode, setDarkMode] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            if (params.get('theme') === 'dark') return true;
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        return false;
    });
    
    // Sophisticated Resize Logic for Embeds
    useEffect(() => {
        const sendHeight = () => {
            const rootElement = document.getElementById('root');
            if (rootElement) {
                const height = rootElement.scrollHeight;
                try {
                    window.parent.postMessage({ type: 'CALCULATOR_RESIZE', height }, '*');
                } catch (e) {
                    // Ignore cross-origin errors
                }
            }
        };

        sendHeight();
        
        const resizeObserver = new ResizeObserver((entries) => {
            if (!entries || entries.length === 0) return;
            window.requestAnimationFrame(() => {
                sendHeight();
            });
        });

        const root = document.getElementById('root');
        if (root) {
            resizeObserver.observe(root);
        }
        
        window.addEventListener('resize', sendHeight);
        
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', sendHeight);
        };
    }, [mode, darkMode]);

    const handleTabChange = (newMode: CalculatorMode) => {
        setMode(newMode);
        const url = new URL(window.location.href);
        url.searchParams.set('tab', newMode);
        window.history.replaceState({}, '', url.toString());
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
    };

    const renderTab = (tabMode: CalculatorMode, title: string) => (
        <button
          onClick={() => handleTabChange(tabMode)}
          className={`px-3 sm:px-6 py-3 font-bold text-xs sm:text-base rounded-t-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-bp-dark-blue ${
            mode === tabMode 
              ? 'bg-white dark:bg-slate-800 text-bp-dark-blue dark:text-bp-light-blue border-b-0 -mb-px' 
              : 'bg-gray-100 dark:bg-slate-900 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-800'
          }`}
        >
          {title}
        </button>
    );

    return (
        <div className={darkMode ? 'dark' : ''}>
            <div className="font-sans bg-gray-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 h-auto min-h-0 overflow-hidden pb-2" id="app-wrapper">
                <main className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto relative">
                    <div className="absolute top-4 right-4 sm:right-8 z-10">
                        <button 
                            onClick={toggleDarkMode} 
                            className="p-2 rounded-full bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
                            aria-label="Toggle Dark Mode"
                        >
                            {darkMode ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                </svg>
                            )}
                        </button>
                    </div>

                    <div className="flex flex-wrap border-b-2 border-gray-200 dark:border-slate-700 mt-8 sm:mt-0">
                        {renderTab(CalculatorMode.BUY_NEW, "Buy with Cash?")}
                        {renderTab(CalculatorMode.PAYOFF, "Pay Off Early?")}
                        {renderTab(CalculatorMode.COST_ESTIMATOR, "Cost Estimator")}
                        {renderTab(CalculatorMode.PMI_REMOVAL, "Kill PMI?")}
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-4 sm:p-6 md:p-8 rounded-b-xl rounded-tr-xl border-x-2 border-b-2 border-gray-200 dark:border-slate-700 shadow-lg transition-colors duration-200">
                        {mode === CalculatorMode.BUY_NEW && <BuyNewHomeCalculator />}
                        {mode === CalculatorMode.PAYOFF && <PayoffCalculator />}
                        {mode === CalculatorMode.COST_ESTIMATOR && <MortgageCostEstimator />}
                        {mode === CalculatorMode.PMI_REMOVAL && <PMIRemovalCalculator />}
                    </div>
                    
                    <footer className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400 max-w-4xl mx-auto px-4">
                        <p>Disclaimer: The calculators and projections provided in this application are for educational and informational purposes only. They rely on user-provided assumptions and historical models, which do not guarantee future results. The output of these tools does not constitute financial, investment, legal, or tax advice. Please consult with a qualified financial advisor or tax professional before making any financial decisions.</p>
                    </footer>
                </main>
            </div>
        </div>
    );
};

export default App;